import * as React from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import classnames from 'classnames';
import { Formik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '@/components/UI/InputTextField';
import CountrySelectField from '@/components/UI/CountrySelectField';
import { RoundButton } from '@/components/UI/RoundButton';
import TextareaField from '@/components/UI/TextareaField';
import SelectField from '@/components/UI/SelectField';
import CheckboxList from '../../CheckboxList';
import { getStartupSectors } from '@/store/services/userServices';
import { PhoneNumberField } from '@/components/UI/PhoneNumberField';
import { BasicUploadField } from '../../../kit/form/BasicUploadField';
import {
  COMPANY_REGISTRY_EXACT_FORMATS,
  DATE_FORMAT_YYYY,
  PHOTO_ALLOWED_FORMATS,
  WEBSITE_REGEX,
} from '@/helpers/common';
import { checkForValidFile } from '@/helpers/utils';
import Notify from '@/components/Notification';
import './index.scss';

const FOUNDATION_YEAR_OPTIONS = new Array(Number(moment().format(DATE_FORMAT_YYYY)) - 1989)
  .fill(0)
  .reduce((options, item, idx) => {
    const year = 1990 + idx;
    options.push({ value: year, label: year });
    return options;
  }, []);

const VALIDATION_SCHEMA = t =>
  Yup.object({
    name: Yup.string()
      .max(100, t('profile_startup_modal.company_name_exceeds_limit'))
      .required(t('profile_startup_modal.company_name_required')),
    full_name: Yup.string().required(t('profile_startup_modal.full_company_name_required')),
    address: Yup.string()
      .max(255, t('profile_startup_modal.address_exceeds_limit'))
      .required(t('profile_startup_modal.address_required')),
    email: Yup.string()
      .email(t('profile_startup_modal.invalid_email'))
      .required(t('profile_startup_modal.email_required')),
    registration_number: Yup.string().required(
      t('profile_startup_modal.registration_number_required'),
    ),
    country: Yup.string().required(t('profile_startup_modal.country_required')),
    description: Yup.string().max(1000, t('profile_startup_modal.description_exceeds_limit')),
    ownership_structure: Yup.string().max(
      1000,
      t('profile_startup_modal.ownership_structure_exceeds_limit'),
    ),
    management_board: Yup.string().max(
      1000,
      t('profile_startup_modal.management_board_exceeds_limit'),
    ),
    site: Yup.string().matches(WEBSITE_REGEX, t('profile_startup_modal.enter_correct_url')),
    phone_number: Yup.number().required(t('profile_startup_modal.phone_number_required')),
    is_valid_phone: Yup.bool()
      .oneOf([true], t('profile_startup_modal.invalid_phone_number'))
      .required(),
    companyRegistry: Yup.array()
      .min(1, t('profile_startup_modal.company_registry_exact_required'))
      .required(t('profile_startup_modal.company_registry_exact_required')),
  });

const ProfileStartupForm = ({ company, location, onSubmit, onCancel }) => {
  const { t } = useTranslation('components');
  const [sectorsList, setSectorsList] = React.useState([]);

  useEffect(() => {
    getStartupSectors().then(res => res && res.success && setSectorsList(res.data));
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        logo: null,
        logoURL: company.image && company.image.file,
        name: (company && company.name) || '',
        full_name: (company && company.full_name) || '',
        address: (company && company.address) || '',
        email: (company && company.email) || '',
        registration_number: (company && company.registration_number) || '',
        country: (company && company.country) || (location && location.country) || '',
        country_code:
          (company && company.country_code) || (location && location.countryCode) || 'RU',
        phone_number: (company && company.phone_number) || '',
        is_valid_phone: true,
        sectors: (company && company.sectors) || [],
        foundation_year: company.foundation_date
          ? moment(company.foundation_date).format(DATE_FORMAT_YYYY)
          : '',
        site: (company && company.site) || '',
        description: (company && company.description) || '',
        ownership_structure: company?.ownership_structure || '',
        management_board: company?.management_board || '',
        promoImage: null,
        promoImageURL: company.promo && company.promo.image && company.promo.image.file,
        companyRegistry: company?.company_registry_exact ? [company?.company_registry_exact] : [],
      }}
      validationSchema={VALIDATION_SCHEMA(t)}
      onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
    >
      {({ values, errors, touched, setValues, handleChange, handleSubmit, setFieldValue }) => (
        <form className="profile-startup-form" onSubmit={handleSubmit}>
          <h1 className="profile-startup-form__title f-32 f-500">
            {t('profile_startup_modal.edit_startup')}
          </h1>

          <InputTextField
            label={t('profile_startup_modal.company_name')}
            name="name"
            value={values.name}
            onChange={handleChange}
            onClear={() => setFieldValue('name', '')}
            error={errors.name && touched.name && errors.name}
            className="profile-startup-form__field"
          />

          <InputTextField
            label={t('profile_startup_modal.legal_company_name')}
            name="full_name"
            value={values.full_name}
            onChange={handleChange}
            onClear={() => setFieldValue('full_name', '')}
            error={errors.full_name && touched.full_name && errors.full_name}
            className="profile-startup-form__field"
          />

          <SelectField
            label={t('profile_startup_modal.foundation_date')}
            name="foundation_year"
            value={values.foundation_year}
            onChange={handleChange}
            error={errors.foundation_year && touched.foundation_year && errors.foundation_year}
            className="profile-personal-form__field"
            options={FOUNDATION_YEAR_OPTIONS}
          />

          <CountrySelectField
            label={t('profile_startup_modal.country')}
            name="country"
            value={values.country_code}
            onChange={(country, code) => {
              setFieldValue('country', country);
              setFieldValue('country_code', code);
            }}
            error={errors.country && touched.country && errors.country}
            className="profile-startup-form__field"
          />

          <InputTextField
            label={t('profile_startup_modal.address')}
            name="address"
            value={values.address}
            onChange={handleChange}
            onClear={() => setFieldValue('address', '')}
            error={errors.address && touched.address && errors.address}
            className="profile-startup-form__field"
          />

          <InputTextField
            label={t('profile_startup_modal.registration_number')}
            name="registration_number"
            value={values.registration_number}
            onChange={handleChange}
            onClear={() => setFieldValue('registration_number', '')}
            error={
              errors.registration_number &&
              touched.registration_number &&
              errors.registration_number
            }
            className="profile-startup-form__field"
          />

          <div className="profile-startup-form__group">
            <BasicUploadField
              name="companyRegistry"
              label={t('profile_startup_modal.download_registry_extract')}
              value={values.companyRegistry}
              acceptFormat={COMPANY_REGISTRY_EXACT_FORMATS}
              multiple={false}
              isRequired
              error={errors.companyRegistry && touched.companyRegistry && errors.companyRegistry}
              onError={message => console.log('eer', message)}
              className="profile-startup-form__company_registry"
              onRemove={() => setFieldValue('companyRegistry', [])}
              onChange={files => setFieldValue('companyRegistry', files)}
            />
          </div>

          <InputTextField
            label={t('profile_startup_modal.company_email')}
            name="email"
            value={values.email}
            onChange={handleChange}
            onClear={() => setFieldValue('email', '')}
            error={errors.email && touched.email && errors.email}
            className="profile-startup-form__field"
          />

          <PhoneNumberField
            label={t('profile_startup_modal.phone_number')}
            name="phone_number"
            value={values.phone_number}
            countryCode={values.country_code}
            onChange={phone_number => setFieldValue('phone_number', phone_number)}
            onClear={() => setFieldValue('phone_number', '')}
            error={
              (errors.phone_number || errors.is_valid_phone) &&
              (touched.phone_number || touched.is_valid_phone) &&
              (errors.phone_number || errors.is_valid_phone)
            }
            isValid={values.is_valid_phone}
            formikBag={{ setFieldValue, touched }}
            containerID="companyPhone"
            className="profile-startup-form__field"
          />

          <InputTextField
            label={t('profile_startup_modal.company_website')}
            name="site"
            value={values.site}
            onChange={handleChange}
            onClear={() => setFieldValue('site', '')}
            error={errors.site && touched.site && errors.site}
            className="profile-startup-form__field"
          />

          <TextareaField
            label={t('profile_startup_modal.brief_description')}
            name="description"
            minRows={4}
            value={values.description}
            onChange={handleChange}
            limit={1000}
            error={errors.description && touched.description && errors.description}
            className="profile-personal-form__field"
          />

          <div className="profile-startup-form__group">
            <TextareaField
              label={t('profile_startup_modal.ownership_structure_description')}
              name="ownership_structure"
              minRows={4}
              value={values.ownership_structure}
              onChange={handleChange}
              limit={1000}
              error={
                errors.ownership_structure &&
                touched.ownership_structure &&
                errors.ownership_structure
              }
            />
          </div>

          <div className="profile-startup-form__group">
            <TextareaField
              label={t('profile_startup_modal.management_board_description')}
              name="management_board"
              minRows={4}
              value={values.management_board}
              onChange={handleChange}
              limit={1000}
              error={errors.management_board && touched.management_board && errors.management_board}
            />
          </div>

          <CheckboxList
            label={t('profile_startup_modal.sectors')}
            list={sectorsList}
            selected={values.sectors.map(item => item.id)}
            className="profile-personal-form__field"
            onChange={value => {
              const selections = values.sectors.map(item => item.id);
              selections.includes(value.id)
                ? setFieldValue(
                    'sectors',
                    values.sectors.filter(item => item.id !== value.id),
                  )
                : setFieldValue('sectors', [...values.sectors, value]);
            }}
          />

          <div className="profile-startup-form__logo">
            <div
              className={classnames(
                'profile-startup-form__logo-title',
                values.logo ? 'f-12' : ' f-16',
              )}
            >
              {t('profile_startup_modal.download_brand_logo')}
            </div>
            {values.logoURL && (
              <div className="profile-startup-form__logo-preview">
                <img src={values.logoURL} alt="preview" />
              </div>
            )}
            <label
              htmlFor="logo"
              className={classnames(
                'profile-startup-form__logo-upload f-14',
                !values.logo && !values.logoURL && 'button',
              )}
            >
              <input
                type="file"
                name="logo"
                id="logo"
                accept="image/jpeg,image/png,image/webp"
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const { isValid } = checkForValidFile(file, PHOTO_ALLOWED_FORMATS);
                  if (!isValid) {
                    Notify.error({ text: t('profile_startup_modal.invalid_image_format') });
                    e.target.value = '';
                    return;
                  }
                  setValues({
                    ...values,
                    logo: file,
                    logoURL: URL.createObjectURL(file),
                  });
                }}
              />
              {t('profile_startup_modal.select_logo')}
            </label>
            {(values.logo || values.logoURL) && (
              <button
                type="button"
                onClick={() => setValues({ ...values, logo: null, logoURL: null })}
                className="profile-startup-form__logo-remove f-14"
              >
                {t('profile_startup_modal.remove')}
              </button>
            )}
            <div className="profile-startup-form__logo-hint f-12">
              {t('profile_startup_modal.allowed_formats')}
            </div>
          </div>

          <div className="profile-startup-form__promo">
            <div
              className={classnames(
                'profile-startup-form__promo-title',
                values.logo ? 'f-12' : ' f-16',
              )}
            >
              {t('profile_startup_modal.download_promo_pictures')}
            </div>
            {values.promoImageURL && (
              <div className="profile-startup-form__promo-preview">
                <img src={values.promoImageURL} alt="Promo preview" />
              </div>
            )}
            <label
              htmlFor="promoImage"
              className={classnames(
                'profile-startup-form__promo-upload f-14',
                !values.promoImage && !values.promoImageURL && 'button',
              )}
            >
              <input
                type="file"
                name="promoImage"
                id="promoImage"
                accept="image/jpeg,image/png,image/webp"
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const { isValid } = checkForValidFile(file, PHOTO_ALLOWED_FORMATS);
                  if (!isValid) {
                    Notify.error({ text: t('profile_startup_modal.invalid_image_format') });
                    e.target.value = '';
                    return;
                  }
                  setValues({
                    ...values,
                    promoImage: file,
                    promoImageURL: URL.createObjectURL(file),
                  });
                }}
              />
              {t('profile_startup_modal.select_image')}
            </label>
            {(values.promoImage || values.promoImageURL) && (
              <button
                type="button"
                onClick={() =>
                  setValues({
                    ...values,
                    promoImage: null,
                    promoImageURL: null,
                  })
                }
                className="profile-startup-form__promo-remove f-14"
              >
                {t('profile_startup_modal.remove')}
              </button>
            )}
            <div className="profile-startup-form__promo-hint f-12">
              {t('profile_startup_modal.allowed_formats')}
            </div>
          </div>

          <div className="profile-startup-form__buttons">
            {onCancel && (
              <RoundButton
                label={t('profile_startup_modal.cancel')}
                type="button"
                fullWidth
                onClick={onCancel}
                className="profile-startup-form__button"
              />
            )}
            <RoundButton
              label={t('profile_startup_modal.save')}
              type="submit"
              onSubmit={handleSubmit}
              fillBackground
              fullWidth
              className="profile-startup-form__button"
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default ProfileStartupForm;
